import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Crosshair } from 'lucide-react';
import { Geolocation } from '@capacitor/geolocation';
import { toast } from '@shared/ui/feedback/Toast';

export interface PinCoords {
  lat: number;
  lng: number;
}

/** A fallback view roughly centred on South Africa when no pin is set yet. */
const DEFAULT_CENTER: [number, number] = [-29.0, 24.0];

/** Maroon teardrop marker — drawn as an SVG divIcon so no image assets are needed. */
function pinIcon(): L.DivIcon {
  return L.divIcon({
    className: '',
    html:
      '<svg width="32" height="32" viewBox="0 0 24 24" fill="#b4544f" stroke="#ffffff" ' +
      'stroke-width="1.5" style="filter:drop-shadow(0 2px 3px rgba(0,0,0,0.35))">' +
      '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>' +
      '<circle cx="12" cy="10" r="2.6" fill="#ffffff" stroke="none"/></svg>',
    iconSize: [32, 32],
    iconAnchor: [16, 30],
  });
}

/**
 * Interactive map for pinning a location. The pin can be placed by tapping
 * the map, dragging the marker, or — the primary path — the "Use my current
 * location" button which captures the device GPS.
 */
export function LocationPinPicker({
  value,
  onChange,
}: {
  value: PinCoords | null;
  onChange: (coords: PinCoords) => void;
}): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [locating, setLocating] = useState(false);

  // Initialise the Leaflet map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const start = value;
    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView(start ? [start.lat, start.lng] : DEFAULT_CENTER, start ? 16 : 5);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker(start ? [start.lat, start.lng] : DEFAULT_CENTER, {
      draggable: true,
      icon: pinIcon(),
      opacity: start ? 1 : 0.45,
    }).addTo(map);

    marker.on('dragend', () => {
      const ll = marker.getLatLng();
      marker.setOpacity(1);
      onChangeRef.current({ lat: ll.lat, lng: ll.lng });
    });
    map.on('click', (e: L.LeafletMouseEvent) => {
      marker.setLatLng(e.latlng);
      marker.setOpacity(1);
      onChangeRef.current({ lat: e.latlng.lat, lng: e.latlng.lng });
    });

    mapRef.current = map;
    markerRef.current = marker;
    // The container may still be settling into layout — recalc after a tick.
    setTimeout(() => map.invalidateSize(), 120);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflect external value changes (e.g. "Use my current location"). Skip when
  // the marker is already there — that's just an echo of a drag/tap.
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!value || !map || !marker) return;
    const current = marker.getLatLng();
    const unchanged =
      Math.abs(current.lat - value.lat) < 1e-6 && Math.abs(current.lng - value.lng) < 1e-6;
    marker.setLatLng([value.lat, value.lng]);
    marker.setOpacity(1);
    if (!unchanged) {
      map.setView([value.lat, value.lng], Math.max(map.getZoom(), 16));
    }
  }, [value]);

  const useMyLocation = async () => {
    setLocating(true);
    try {
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 8000 });
      onChangeRef.current({ lat: pos.coords.latitude, lng: pos.coords.longitude });
    } catch {
      toast.danger('Location unavailable', 'Allow location access, or tap the map to drop a pin.');
    } finally {
      setLocating(false);
    }
  };

  return (
    <div className="flex flex-col gap-2.5">
      <button
        type="button"
        onClick={() => void useMyLocation()}
        disabled={locating}
        className="flex items-center justify-center gap-2 rounded-pill bg-primary-700 px-4 py-2.5 text-body-sm font-semibold text-white disabled:opacity-60"
      >
        <Crosshair className="h-4 w-4" strokeWidth={1.75} />
        {locating ? 'Locating…' : 'Use my current location'}
      </button>

      <div
        ref={containerRef}
        className="isolate h-52 w-full overflow-hidden rounded-lg border border-hairline-strong"
        aria-label="Location map"
      />

      <p className="text-caption text-ink-muted">
        {value
          ? `Pinned at ${value.lat.toFixed(5)}, ${value.lng.toFixed(5)} — drag the pin or tap the map to adjust.`
          : 'Tap the map or use your location to drop a pin.'}
      </p>
    </div>
  );
}
